import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { TrabajadorDeEmpresa } from '@copathy/shared';
import { ErrorApi } from '../lib/api';
import {
  useCliente,
  useCodigoRegistro,
  useGuardarCliente,
  useTrabajadoresDeCliente,
  type Cliente,
  type FiltroActivo,
} from '../lib/clientes';
import { useMe, usePuede } from '../lib/auth';
import { useMigas } from '../lib/migas';
import { Contenedor } from '../componentes/Contenedor';
import { BotonVolver } from '../componentes/BotonVolver';
import { Boton } from '../componentes/Boton';
import { FormularioCliente } from '../componentes/FormularioCliente';
import { FormularioUsuario } from '../componentes/FormularioUsuario';
import { EnlaceDeInvitacion } from '../componentes/EnlaceDeInvitacion';
import { useUsuarios } from '../lib/usuarios';
import { LogoCliente } from '../componentes/LogoCliente';
import { nombrePais } from '../lib/paises';
import { Escudo, Llave, Personas } from '../componentes/iconos';

const fecha = (iso: string) =>
  new Date(iso).toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });

/**
 * El código se muestra partido en dos mitades de cuatro.
 *
 * El esquema del registro quita los espacios antes de comparar, así que quien lo
 * copie tal cual lo ve —con el espacio— entra igual. Ocho caracteres seguidos se
 * dictan mal por teléfono.
 *
 * ⚠️ Solo se parte si mide exactamente 8. Los códigos cargados a mano antes de
 * este módulo tienen otro largo, y partirlos igual daba cosas como `AVIL A-2026`:
 * se lee como si el espacio fuera parte del código.
 */
const legible = (codigo: string) =>
  codigo.length === 8 ? `${codigo.slice(0, 4)} ${codigo.slice(4)}` : codigo;

/**
 * Detalle de un cliente (CC-01).
 *
 * Es una **página con ruta propia**, no un modal, y eso no contradice la regla de
 * «modal en escritorio, página en móvil»: esa regla es para **formularios**. Acá
 * hay datos, credencial de registro, gente y —cuando exista M4— programas
 * contratados. Eso no cabe en un modal, y además tiene que poder compartirse por
 * enlace y recargarse.
 */
export function ClienteDetalle() {
  const { id } = useParams<{ id: string }>();
  const clienteId = Number(id);
  const valido = Number.isInteger(clienteId) && clienteId > 0;

  const consulta = useCliente(valido ? clienteId : null);
  const cliente = consulta.data;

  useMigas([{ texto: 'Clientes', a: '/clientes' }, { texto: cliente?.nombre ?? 'Cliente' }]);

  const [editando, setEditando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!valido || (consulta.isError && !cliente)) {
    return (
      <Contenedor>
        <div className="rounded-2xl border border-marino-100 bg-white py-16 text-center">
          <h1 className="font-semibold text-marino-700">No encontramos ese cliente</h1>
          <p className="mt-1 text-sm text-marino-400">
            Puede que lo hayan quitado, o que la dirección esté mal escrita.
          </p>
          <div className="mt-5 flex justify-center">
            <BotonVolver a="/clientes">Volver a Clientes</BotonVolver>
          </div>
        </div>
      </Contenedor>
    );
  }

  return (
    <Contenedor>
      <BotonVolver a="/clientes">Clientes</BotonVolver>

      {consulta.isPending && <p className="py-16 text-center text-marino-400">Cargando…</p>}

      {error && (
        <div
          role="alert"
          className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      {cliente && (
        <>
          <Encabezado cliente={cliente} alEditar={() => setEditando(true)} />
          <div className="mt-6 grid gap-5 lg:grid-cols-5">
            <div className="space-y-5 lg:col-span-3">
              <Datos cliente={cliente} />
              <Administradores clienteId={cliente.id} clienteNombre={cliente.nombre} />
              <Usuarios clienteId={cliente.id} />
            </div>
            <div className="space-y-5 lg:col-span-2">
              <CodigoRegistro cliente={cliente} alFallar={setError} />
              <Estado cliente={cliente} alFallar={setError} />
            </div>
          </div>

          <FormularioCliente
            objetivo={editando ? cliente : null}
            alCerrar={() => setEditando(false)}
            alFallar={setError}
          />
        </>
      )}
    </Contenedor>
  );
}

// ---------------------------------------------------------------------------

function Encabezado({ cliente, alEditar }: { cliente: Cliente; alEditar: () => void }) {
  const puedeEditar = usePuede('empresas.editar');

  return (
    <header className="mt-4 flex flex-wrap items-start gap-4">
      <LogoCliente
        clienteId={cliente.id}
        nombre={cliente.nombre}
        tieneLogo={cliente.tieneLogo}
        tamano="detalle"
      />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-2xl font-bold text-marino-800">{cliente.nombre}</h1>
          {!cliente.activo && (
            <span className="rounded-full bg-marino-50 px-2.5 py-0.5 text-xs font-semibold text-marino-500">
              Desactivado
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-marino-400">
          {[cliente.rif, cliente.sector].filter(Boolean).join(' · ') ||
            'Sin datos fiscales cargados'}{' '}
          · Cliente desde {fecha(cliente.creadoEn)}
        </p>
      </div>

      {puedeEditar && (
        <Boton variante="secundario" onClick={alEditar} className="px-4 py-2.5 text-sm">
          Editar
        </Boton>
      )}
    </header>
  );
}

function Panel({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-marino-100 bg-white p-5">
      <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-marino-400">
        {titulo}
      </h2>
      {children}
    </section>
  );
}

function Dato({ titulo, valor }: { titulo: string; valor: string | null }) {
  return (
    <div className="flex flex-col">
      <dt className="text-marino-400">{titulo}</dt>
      <dd className="font-medium text-marino-800">{valor || '—'}</dd>
    </div>
  );
}

function Datos({ cliente }: { cliente: Cliente }) {
  return (
    <Panel titulo="Datos del cliente">
      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
        <Dato titulo="Identificación fiscal" valor={cliente.rif} />
        <Dato titulo="Sector" valor={cliente.sector} />
        <Dato titulo="País" valor={cliente.pais ? nombrePais(cliente.pais) : null} />
        <Dato
          titulo="Empleados"
          valor={cliente.empleados === null ? null : cliente.empleados.toLocaleString('es-VE')}
        />
        <Dato titulo="Contacto" valor={cliente.contactoNombre} />
        <Dato titulo="Correo del contacto" valor={cliente.contactoCorreo} />
        <Dato titulo="Teléfono" valor={cliente.contactoTelefono} />
        <Dato titulo="Última actualización" valor={fecha(cliente.actualizadoEn)} />
      </dl>
      {/*
        TODO: los programas contratados entran cuando exista M4. Son una relación
        cliente ↔ programa con fechas, no un campo de esta ficha (D-034).
      */}
    </Panel>
  );
}

// ---------------------------------------------------------------------------

const ETIQUETA_CUENTA: Record<string, { texto: string; clases: string }> = {
  activo: { texto: 'Activa', clases: 'bg-green-50 text-green-800' },
  sin_activar: { texto: 'Sin activar', clases: 'bg-amber-50 text-amber-800' },
  desactivado: { texto: 'Desactivada', clases: 'bg-marino-50 text-marino-500' },
};

/**
 * Los administradores del cliente: las cuentas con las que su gente de RRHH entra
 * a ver sus resultados.
 *
 * Va aparte de la lista de trabajadores porque son cosas distintas: el trabajador
 * responde; el administrador mira agregados. Y hasta este módulo **no había forma
 * de crear ninguno** — el rol existía en la base y era inalcanzable.
 */
function Administradores({ clienteId, clienteNombre }: { clienteId: number; clienteNombre: string }) {
  const [creando, setCreando] = useState(false);
  const [enlace, setEnlace] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const puede = usePuede('usuarios.administrar');
  const { usuario: yo } = useMe();
  // El cliente va DENTRO de los filtros: `useUsuarios` los recibe todos en la
  // misma bolsa, que es lo mismo que viaja en la query.
  const consulta = useUsuarios('', {
    rol: 'admin_empresa',
    ...(puede ? { empresaId: String(clienteId) } : {}),
  });

  if (!puede) return null;

  return (
    <Panel titulo="Administradores del cliente">
      {error && (
        <div role="alert" className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}
      {enlace && <EnlaceDeInvitacion enlace={enlace} alCerrar={() => setEnlace(null)} />}

      {consulta.data?.datos.length === 0 && (
        <p className="text-sm text-marino-500">
          Este cliente no tiene a nadie que pueda ver sus resultados.
        </p>
      )}

      <ul className="divide-y divide-marino-100">
        {consulta.data?.datos.map((u) => (
          <li key={u.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-marino-800">{u.nombre}</p>
              <p className="truncate text-sm text-marino-400">{u.correo}</p>
            </div>
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                ETIQUETA_CUENTA[u.estado]?.clases ?? 'bg-marino-50 text-marino-500'
              }`}
            >
              {ETIQUETA_CUENTA[u.estado]?.texto ?? u.estado}
            </span>
          </li>
        ))}
      </ul>

      <Boton
        variante="secundario"
        className="mt-4 px-3.5 py-2 text-sm"
        onClick={() => setCreando(true)}
      >
        Invitar administrador
      </Boton>

      <FormularioUsuario
        objetivo={creando ? 'nuevo' : null}
        copathyId={yo?.empresaId ?? null}
        empresaFija={{ id: clienteId, nombre: clienteNombre }}
        alCerrar={() => setCreando(false)}
        alFallar={setError}
        alInvitar={(e) => e && setEnlace(e)}
      />
    </Panel>
  );
}

function Usuarios({ clienteId }: { clienteId: number }) {
  const [activo, setActivo] = useState<FiltroActivo>('si');
  const puedeVer = usePuede('trabajadores.ver');
  const consulta = useTrabajadoresDeCliente(puedeVer ? clienteId : null, activo);

  if (!puedeVer) return null;

  return (
    <Panel titulo="Usuarios del cliente">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {(['si', 'no', 'todas'] as FiltroActivo[]).map((v) => (
          <button
            key={v}
            onClick={() => setActivo(v)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
              activo === v
                ? 'bg-marino-800 text-white'
                : 'bg-white text-marino-500 ring-1 ring-marino-200 hover:text-marino-800'
            }`}
          >
            {v === 'si' ? 'Activos' : v === 'no' ? 'Desactivados' : 'Todos'}
          </button>
        ))}
        <span className="ml-auto text-xs text-marino-400">
          {consulta.data ? `${consulta.data.total} en total` : ''}
        </span>
      </div>

      {consulta.isPending && <p className="py-8 text-center text-sm text-marino-400">Cargando…</p>}
      {consulta.isError && (
        <p className="py-8 text-center text-sm text-red-600">No pudimos cargar los usuarios.</p>
      )}

      {consulta.data?.datos.length === 0 && (
        <div className="py-10 text-center">
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-marino-50 text-marino-300">
            <Personas size={20} />
          </div>
          <p className="text-sm text-marino-500">
            {activo === 'si'
              ? 'Este cliente todavía no tiene gente adentro.'
              : 'No hay usuarios con ese filtro.'}
          </p>
          <p className="mt-1 text-xs text-marino-400">
            Se registran solos con el código, y entran cuando COPATHY los aprueba.
          </p>
        </div>
      )}

      <ul className="divide-y divide-marino-100">
        {consulta.data?.datos.map((t: TrabajadorDeEmpresa) => (
          <li key={t.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-marino-800">{t.nombre}</p>
              <p className="truncate text-sm text-marino-400">{t.correo}</p>
            </div>
            <p className="text-sm text-marino-500">
              {[t.area, t.cargo].filter(Boolean).join(' · ') || 'Sin área ni cargo asignados'}
            </p>
            {t.estadoUsuario && (
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                  ETIQUETA_CUENTA[t.estadoUsuario]?.clases ?? 'bg-marino-50 text-marino-500'
                }`}
              >
                {ETIQUETA_CUENTA[t.estadoUsuario]?.texto ?? t.estadoUsuario}
              </span>
            )}
          </li>
        ))}
      </ul>

      {/*
        Se ven la ficha y el estado de la cuenta. NUNCA lo que respondieron: para
        eso está `respuestas.ver_individuales`, que es otro permiso y otra pantalla
        (D-009). Esta lista no es una puerta lateral a la frontera 3.
      */}
    </Panel>
  );
}

// ---------------------------------------------------------------------------

function CodigoRegistro({
  cliente,
  alFallar,
}: {
  cliente: Cliente;
  alFallar: (m: string | null) => void;
}) {
  const { rotar, cerrar } = useCodigoRegistro();
  const puede = usePuede('codigos.administrar');
  const [copiado, setCopiado] = useState(false);

  async function accion(fn: () => Promise<Cliente>) {
    alFallar(null);
    try {
      await fn();
    } catch (e) {
      alFallar(e instanceof ErrorApi ? e.message : 'No pudimos completar la operación.');
    }
  }

  return (
    <Panel titulo="Código de registro">
      {/* Sin encabezado propio: el `Panel` ya pone uno, y dos seguidos con el
          mismo texto se le anuncian dos veces a un lector de pantalla. */}
      {cliente.codigoRegistro ? (
        <>
          <p className="font-mono text-2xl font-bold tracking-widest text-marino-900">
            {legible(cliente.codigoRegistro)}
          </p>
          <p className="mt-1 text-xs text-marino-400">
            {cliente.codigoRegistroExpiraEn
              ? `Vence el ${fecha(cliente.codigoRegistroExpiraEn)}.`
              : 'No vence.'}
          </p>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard?.writeText(cliente.codigoRegistro ?? '');
              setCopiado(true);
              setTimeout(() => setCopiado(false), 2000);
            }}
            className="mt-2 text-sm font-semibold text-marino-600 transition hover:text-naranja-600 hover:underline"
          >
            {copiado ? 'Copiado' : 'Copiar'}
          </button>
        </>
      ) : (
        <p className="flex items-center gap-2 text-sm text-marino-500">
          <Llave size={17} className="shrink-0 text-marino-300" />
          Sin código: nadie puede pedir acceso todavía.
        </p>
      )}

      {/*
        El código no da acceso por sí solo (D-023): dice a qué cliente pertenece la
        solicitud, y el acceso sigue dependiendo de que Copathy apruebe. Se dice acá
        porque quien lo reparte tiene que saberlo.
      */}
      <p className="mt-3 text-xs text-marino-500">
        El código no da acceso: solo indica a qué cliente pertenece la solicitud. Cada persona
        sigue esperando la aprobación de COPATHY.
      </p>

      {puede && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Boton
            onClick={() =>
              void accion(() => rotar.mutateAsync({ id: cliente.id, diasVigencia: null }))
            }
            cargando={rotar.isPending}
            disabled={!cliente.activo}
            className="px-3.5 py-2 text-sm"
          >
            {cliente.codigoRegistro ? 'Rotar código' : 'Generar código'}
          </Boton>
          {cliente.codigoRegistro && (
            <Boton
              variante="secundario"
              onClick={() => void accion(() => cerrar.mutateAsync(cliente.id))}
              cargando={cerrar.isPending}
              className="px-3.5 py-2 text-sm"
            >
              Cerrar
            </Boton>
          )}
        </div>
      )}

      {!cliente.activo && (
        <p className="mt-3 text-xs text-amber-800">
          El cliente está desactivado: no se le puede dar un código nuevo.
        </p>
      )}

      {cliente.solicitudesPendientes > 0 && (
        <Link
          to="/solicitudes"
          className="mt-4 flex items-center gap-2 rounded-xl border border-naranja-200 bg-naranja-50 px-3.5 py-2.5 text-sm font-semibold text-naranja-700 transition hover:bg-naranja-100"
        >
          <Escudo size={16} />
          {cliente.solicitudesPendientes} por revisar en la cola
        </Link>
      )}
    </Panel>
  );
}

function Estado({ cliente, alFallar }: { cliente: Cliente; alFallar: (m: string | null) => void }) {
  const { editar } = useGuardarCliente();
  const puedeEditar = usePuede('empresas.editar');
  if (!puedeEditar) return null;

  return (
    <Panel titulo={cliente.activo ? 'Desactivar cliente' : 'Reactivar cliente'}>
      <p className="text-sm text-marino-500">
        {cliente.activo
          ? 'Su gente deja de poder entrar y se cierra su código de registro. Nada se borra: los datos y las respuestas se conservan.'
          : 'Su gente vuelve a poder entrar. El código sigue cerrado hasta que le generes uno nuevo.'}
      </p>
      <Boton
        variante="secundario"
        className="mt-3 px-3.5 py-2 text-sm"
        cargando={editar.isPending}
        onClick={() => {
          alFallar(null);
          editar
            .mutateAsync({ id: cliente.id, cambios: { activo: !cliente.activo } })
            .catch((e: unknown) =>
              alFallar(e instanceof ErrorApi ? e.message : 'No pudimos cambiar el estado.'),
            );
        }}
      >
        {cliente.activo ? 'Desactivar' : 'Reactivar'}
      </Boton>
    </Panel>
  );
}
